import { PageController } from '@client/components/_core/layout/PageController';
import { VCheckbox } from '@client/components/_shared/forms/view-form/VCheckbox';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { VUploadControl } from '@client/components/_shared/forms/view-form/VUploadControl';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { productVersionFormStore } from '@client/components/products-v2/store';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Tag } from 'antd';
import { Link2, Plus, X } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/documents',
)({
    staticData: {
        ui: {
            useParentTitle: false,
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <Documents />
        </PageController>
    );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            className="flex size-6 cursor-pointer items-center justify-center rounded border-none bg-transparent text-muted-foreground hover:bg-muted"
            onClick={onClick}
        >
            <X className="size-4" />
        </button>
    );
}

function Documents() {
    const formData = productVersionFormStore.use.formData();
    const isReadOnly = productVersionFormStore.use.isReadonly();
    const save = productVersionFormStore.use.save();

    const [isPdsSectionVisibleState, setIsPdsSectionVisibleState] = useState(
        formData.isPdsIncludedInPolicyWording || formData.pdsFile !== null,
    );
    const [isSupplementaryPdsSectionVisibleState, setIsSupplementaryPdsSectionVisibleState] = useState(
        formData.supplementaryPdsFile !== null,
    );

    const isPdsSectionVisible =
        isPdsSectionVisibleState || formData.isPdsIncludedInPolicyWording || formData.pdsFile !== null;
    const isSupplementaryPdsSectionVisible =
        isSupplementaryPdsSectionVisibleState || formData.supplementaryPdsFile !== null;

    return (
        <div className="max-w-2xl space-y-4">
            <Card
                size="small"
                title="Policy wording"
                classNames={{
                    body: 'space-y-4',
                }}
            >
                <VTextInput label="Name" field="policyWording" formStore={productVersionFormStore} />
                <VUploadControl
                    field="policyWordingFile"
                    formStore={productVersionFormStore}
                    accept=".pdf"
                    file={formData.policyWordingFile}
                    onSave={async (values) => {
                        await save({
                            policyWordingFile: {
                                key: values.key,
                                name: values.name,
                                description: values.description ?? null,
                                categories: values.categories ?? [],
                                mimeType: values.mimeType ?? null,
                                size: values.size,
                                additionalInfo: values.additionalInfo,
                            },
                        });
                    }}
                    onClear={async () => {
                        await save({
                            policyWordingFile: null,
                        });
                    }}
                />
                <div className="mt-3 flex items-center gap-4">
                    <VCheckbox
                        label="Includes PDS"
                        labelPosition="right"
                        field="isPdsIncludedInPolicyWording"
                        formStore={productVersionFormStore}
                        isReadOnly={!!formData.pdsFile}
                    />
                    {formData.pdsFile && (
                        <div className="mt-1 text-muted-foreground text-xs">Remove the standalone PDS first</div>
                    )}
                </div>
            </Card>

            {isPdsSectionVisible && (
                <Card
                    size="small"
                    classNames={{
                        title: 'flex items-center gap-2',
                        body: 'space-y-4',
                    }}
                    title={
                        <>
                            PDS
                            {formData.isPdsIncludedInPolicyWording && (
                                <Tag color="purple" variant="filled" icon={<Link2 className="size-3" />}>
                                    Linked to wording
                                </Tag>
                            )}
                        </>
                    }
                    extra={
                        !isReadOnly &&
                        !formData.isPdsIncludedInPolicyWording && (
                            <RemoveButton
                                onClick={async () => {
                                    await save({
                                        pdsFile: null,
                                        pdsName: null,
                                    });
                                }}
                            />
                        )
                    }
                >
                    <VTextInput label="Name" field="pdsName" formStore={productVersionFormStore} />
                    {formData.isPdsIncludedInPolicyWording && formData.policyWordingFile && (
                        <div className="mt-3 flex items-center gap-2 rounded-md border border-separator border-dashed px-3 py-2 opacity-70">
                            <Link2 className="size-3" />
                            <span className="text-muted-foreground text-sm">{formData.policyWordingFile.name}</span>
                        </div>
                    )}
                    {!formData.isPdsIncludedInPolicyWording && (
                        <VUploadControl
                            field="pdsFile"
                            formStore={productVersionFormStore}
                            accept=".pdf"
                            file={formData.pdsFile}
                            onSave={async (values) => {
                                await save({
                                    pdsFile: {
                                        key: values.key,
                                        name: values.name,
                                        description: values.description ?? null,
                                        categories: values.categories ?? [],
                                        mimeType: values.mimeType ?? null,
                                        size: values.size,
                                        additionalInfo: values.additionalInfo,
                                    },
                                });
                            }}
                            onClear={async () => {
                                await save({
                                    pdsFile: null,
                                });
                            }}
                        />
                    )}
                </Card>
            )}

            {isSupplementaryPdsSectionVisible && (
                <Card
                    size="small"
                    title="Supplementary PDS"
                    classNames={{
                        body: 'space-y-4',
                    }}
                    extra={
                        !isReadOnly && (
                            <RemoveButton
                                onClick={async () => {
                                    await save({ supplementaryPdsName: null, supplementaryPdsFile: null });
                                }}
                            />
                        )
                    }
                >
                    <VTextInput label="Name" field="supplementaryPdsName" formStore={productVersionFormStore} />
                    <VUploadControl
                        field="supplementaryPdsFile"
                        formStore={productVersionFormStore}
                        accept=".pdf"
                        file={formData.supplementaryPdsFile}
                        onSave={async (values) => {
                            await save({
                                supplementaryPdsFile: {
                                    key: values.key,
                                    name: values.name,
                                    description: values.description ?? null,
                                    categories: values.categories ?? [],
                                    mimeType: values.mimeType ?? null,
                                    size: values.size,
                                    additionalInfo: values.additionalInfo,
                                },
                            });
                        }}
                        onClear={async () => {
                            await save({
                                supplementaryPdsFile: null,
                            });
                        }}
                    />
                </Card>
            )}

            {(formData.endorsements ?? []).map((item, i) => {
                const endorsements = formData.endorsements ?? [];

                return (
                    <Card
                        key={String(item.id)}
                        size="small"
                        title={`Endorsement ${i + 1}`}
                        classNames={{
                            body: 'space-y-4',
                        }}
                        extra={
                            !isReadOnly && (
                                <RemoveButton
                                    onClick={async () => {
                                        await save({
                                            endorsements: [
                                                ...endorsements.slice(0, i).map((item) => ({ id: item.id })),
                                                ...endorsements.slice(i + 1).map((item) => ({ id: item.id })),
                                            ] as any as typeof formData.endorsements,
                                        });
                                    }}
                                />
                            )
                        }
                    >
                        <VTextInput
                            label="Name"
                            field={`endorsements.${i}.name`}
                            formStore={productVersionFormStore}
                            formatValueFn={() => item.name}
                            onSave={async (value, formStoreActions) => {
                                await formStoreActions.save({
                                    endorsements: [
                                        ...endorsements.slice(0, i).map((item) => ({ id: item.id })),
                                        {
                                            id: item.id,
                                            name: value,
                                        } as any as (typeof formData.endorsements)[number],
                                        ...endorsements.slice(i + 1).map((item) => ({ id: item.id })),
                                    ] as any as typeof formData.endorsements,
                                });
                            }}
                        />
                        <VUploadControl
                            field={`endorsements.${i}.file`}
                            formStore={productVersionFormStore}
                            accept=".pdf"
                            file={item.file}
                            onSave={async (values) => {
                                await save({
                                    endorsements: [
                                        ...endorsements.slice(0, i).map((item) => ({ id: item.id })),
                                        {
                                            ...item,
                                            file: {
                                                key: values.key,
                                                name: values.name,
                                                description: values.description ?? null,
                                                categories: values.categories ?? [],
                                                mimeType: values.mimeType ?? null,
                                                size: values.size,
                                                additionalInfo: values.additionalInfo,
                                            },
                                        },
                                        ...endorsements.slice(i + 1).map((item) => ({ id: item.id })),
                                    ] as any as typeof formData.endorsements,
                                });
                            }}
                            onClear={async () => {
                                await save({
                                    endorsements: [
                                        ...endorsements.slice(0, i).map((item) => ({ id: item.id })),
                                        {
                                            ...item,
                                            file: null,
                                        } as any as (typeof formData.endorsements)[number],
                                        ...endorsements.slice(i + 1),
                                    ] as any as typeof formData.endorsements,
                                });
                            }}
                        />
                    </Card>
                );
            })}

            {!isReadOnly && (
                <div className="flex flex-wrap gap-2">
                    {!isPdsSectionVisible && (
                        <Button icon={<Plus />} onClick={() => setIsPdsSectionVisibleState(true)}>
                            Add PDS
                        </Button>
                    )}
                    {!isSupplementaryPdsSectionVisible && (
                        <Button icon={<Plus />} onClick={() => setIsSupplementaryPdsSectionVisibleState(true)}>
                            Add supplementary PDS
                        </Button>
                    )}
                    <Button
                        icon={<Plus />}
                        onClick={async () =>
                            await save({
                                endorsements: [
                                    ...(formData.endorsements ?? []),
                                    {
                                        id: undefined,
                                        name: '',
                                        file: null,
                                    } as any as (typeof formData.endorsements)[number],
                                ],
                            })
                        }
                    >
                        Add endorsement
                    </Button>
                </div>
            )}
        </div>
    );
}
